import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Section, SectionHeading, FadeIn } from "@/components/Section";
import { Check, ArrowRight } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback used only if the published-tiers endpoint is unreachable.
const FALLBACK_TIERS = [
  {
    id: "connect",
    name: "Nodera Connect",
    tagline: "Connect your clinic to the cash-pay vendor network and run programs without the friction.",
    price_monthly: 299,
    primary_features: [
      "Vendor network + services catalog",
      "Cross-vendor program builder",
      "Unlimited workflows",
      "Consumer roster + intake",
      "Documents & training library",
      "Standard email support",
    ],
    highlight: false,
  },
  {
    id: "grow",
    name: "Nodera Grow",
    tagline: "Add the marketing engine — convert your existing roster and capture new consumers with AI campaigns.",
    price_monthly: 799,
    primary_features: [
      "Everything in Nodera Connect",
      "Marketing Campaigns module",
      "AI Campaign Assistant + Audience Builder",
      "Cohort analytics + MRR Pulse",
      "Custom marketing assets",
      "Priority 24-hour SLA",
    ],
    highlight: true,
  },
  {
    id: "scale",
    name: "Nodera Scale",
    tagline: "Multi-location operations, automation, white-label — for DSOs and clinic groups.",
    price_monthly: 1999,
    primary_features: [
      "Everything in Nodera Grow",
      "Marketing automation engine",
      "Multi-location rollups",
      "White-label clinic portal",
      "SSO + audit log exports",
      "Named CSM + 4-hour SLA",
    ],
    highlight: false,
  },
];

export default function Pricing() {
  const [tiers, setTiers] = useState(FALLBACK_TIERS);
  const [meta, setMeta] = useState({ source: "fallback", version: null, deployed_at: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/public/subscription-tiers`, { timeout: 8000 });
        if (cancelled) return;
        if (r.data?.tiers?.length) {
          setTiers(r.data.tiers);
          setMeta({
            source: r.data.source || "published",
            version: r.data.version || null,
            deployed_at: r.data.deployed_at || null,
          });
        }
      } catch {
        // Fallback already set in initial state — leave as-is.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const display = useMemo(() => {
    return tiers.map((t) => ({
      id: t.id,
      name: t.name,
      tagline: t.tagline,
      // Pricing page emphasises top-line features (limit to 7 for visual balance).
      features: (t.primary_features || []).slice(0, 7),
      highlight: !!t.highlight,
      target_persona: t.target_persona || "",
    }));
  }, [tiers]);

  return (
    <div data-testid="pricing-page">
      <Section className="pt-28 pb-10">
        <SectionHeading
          eyebrow="Pricing"
          title="Plans built for every stage of cash-pay program growth."
          intro="Foundation features (vendor network, program lifecycle, workflow automation, consumer roster) are included in every tier. Higher tiers layer on marketing, AI, automation, multi-location, and enterprise controls."
          align="center"
        />
      </Section>

      <Section className="pt-0">
        <div className="grid lg:grid-cols-3 gap-6" data-testid="pricing-tier-grid">
          {display.map((t, i) => (
            <FadeIn key={t.id} delay={i * 0.08}>
              <div
                data-testid={`pricing-tier-${t.id}`}
                className={`relative h-full rounded-2xl p-8 flex flex-col ${
                  t.highlight
                    ? "bg-slate-900 text-white border border-slate-900 shadow-xl"
                    : "bg-white border border-slate-200"
                }`}
              >
                {t.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] uppercase tracking-[0.18em] font-bold px-3 py-1 rounded-full bg-blue-600 text-white">
                    Most popular
                  </div>
                )}
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold opacity-60">{t.name}</div>
                <p
                  className={`mt-4 text-sm ${t.highlight ? "text-slate-300" : "text-slate-600"}`}
                  data-testid={`pricing-tier-${t.id}-tagline`}
                >
                  {t.tagline}
                </p>
                <div
                  className={`mt-3 text-[11px] uppercase tracking-[0.18em] font-semibold ${
                    t.highlight ? "text-blue-300" : "text-blue-700"
                  }`}
                  data-testid={`pricing-tier-${t.id}-pricing-stance`}
                >
                  Custom pricing · Talk to sales
                </div>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {t.features.map((f, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <span
                        className={`mt-0.5 w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${
                          t.highlight ? "bg-blue-500/20 text-blue-300" : "bg-blue-50 text-blue-600"
                        }`}
                      >
                        <Check className="w-3 h-3" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                {t.target_persona && (
                  <div className={`mt-5 rounded-xl px-3 py-2 text-[11px] ${
                    t.highlight ? "bg-white/10 text-slate-300" : "bg-slate-50 text-slate-600"
                  }`}>
                    <span className="font-bold uppercase tracking-wider opacity-80">Best for</span>
                    <div className="mt-0.5 leading-snug">{t.target_persona}</div>
                  </div>
                )}

                <Link
                  to="/demo"
                  data-testid={`pricing-cta-${t.id}`}
                  onClick={() => trackEvent("pricing_book_demo_cta_clicked", { tier_id: t.id, tier_name: t.name })}
                  className={`mt-6 inline-flex items-center justify-center gap-1.5 rounded-full px-5 py-3 text-sm font-semibold transition-colors ${
                    t.highlight
                      ? "bg-white text-slate-900 hover:bg-slate-100"
                      : "bg-slate-900 text-white hover:bg-slate-800"
                  }`}
                >
                  Talk to sales <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section className="bg-slate-50 border-t border-slate-200">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl font-bold text-slate-900">Frequently asked questions.</h2>
          </div>
          <div className="space-y-5">
            {[
              {
                q: "Is Nodera a healthcare provider or billing company?",
                a: "No. Nodera is operational infrastructure — workflow automation, vendor coordination, and program enablement. We do not custody funds, diagnose, or interpret clinical data.",
              },
              {
                q: "Do you set the prices clinics charge consumers?",
                a: "No. Clinics own consumer-facing pricing entirely. Nodera platform pricing is custom-quoted per clinic during a sales conversation.",
              },
              {
                q: "What does implementation look like?",
                a: "Most clinics activate their first program within 2–4 weeks. Our onboarding is self-serve for Connect, white-glove for Scale.",
              },
              {
                q: "Can I switch tiers later?",
                a: "Yes — upgrade or downgrade any time. Annual commitments unlock additional value; specifics are covered in your sales conversation.",
              },
            ].map((f, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="font-display font-semibold text-slate-900">{f.q}</div>
                <p className="mt-1.5 text-sm text-slate-600">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
}
